import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Heart, Leaf, Lock, Star, Sparkles, CheckCircle2, Users } from "lucide-react";
import ProductCard from "../components/ProductCard";
import FlashSaleBanner from "../components/FlashSaleBanner";
import { fetchProducts, fetchReviews, fetchFaqs } from "../lib/api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const CAMPAIGN_LOW_BATTERY = "/products/campaign-low-battery.jpg";
const CAMPAIGN_PASSWORD = "/products/campaign-password-protected.jpg";
const AMBASSADOR = "/products/ambassador-hero.jpg";
const BRAND_STORY_BG = "/products/brand-story-bg.jpg";
const DARK_SILK = "/products/hero-bg.jpg";
const MONTHLY_BOX = "/products/monthly-box.jpg";

// Official BananaX 4-icon trust badge set (matches brand campaign creative)
const TRUST_BADGES = [
  { icon: ShieldCheck, label: "Premium Quality" },
  { icon: Leaf, label: "Natural Latex" },
  { icon: CheckCircle2, label: "Electronically Tested" },
  { icon: Users, label: "Trusted by Thousands" },
];

const PILLARS = [
  { icon: ShieldCheck, title: "Dermatologically Tested", text: "Cleared by independent dermatologists for sensitive skin." },
  { icon: Heart, title: "Enhanced Comfort", text: "Ergonomic shape and smooth lubrication for natural intimacy." },
  { icon: Leaf, title: "Premium Latex", text: "Sustainably sourced natural latex, refined for whisper-thin feel." },
  { icon: Lock, title: "Discreet Packaging", text: "Unmarked exterior. Quiet courier. No questions asked." },
  { icon: Star, title: "International Standards", text: "100% electronically tested. ISO 4074 & EN ISO 23409 certified." },
];

const TECH = [
  { n: "01", t: "Ultra-thin Technology", d: "0.045mm precision-rolled latex preserves natural sensation while maintaining ISO-certified burst strength." },
  { n: "02", t: "Electronic Quality Testing", d: "Every condom traverses a pinhole-detection chamber. Failures discarded. Only the immaculate ships." },
  { n: "03", t: "Lubrication Technology", d: "Medical-grade silicone glide, formulated to remain neutral in scent and balanced for sensitivity." },
  { n: "04", t: "Natural Latex Sourcing", d: "Hevea brasiliensis from FSC-certified plantations. Refined in low-temperature curing for elasticity." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => {});
    fetchReviews().then(setReviews).catch(() => {});
    fetchFaqs().then(setFaqs).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <div className="pt-16 lg:pt-20">
        <FlashSaleBanner variant="slim" />
      </div>
      {/* HERO — Official campaign: "Nothing should die at the wrong moment" */}
      <section className="relative min-h-screen pt-20 overflow-hidden bg-ink-950">
        <div className="absolute inset-0 grain-overlay"></div>
        <div className="relative max-w-[1440px] mx-auto grid lg:grid-cols-2 min-h-screen">
          {/* Left: campaign creative */}
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4 }}
            className="relative h-[60vh] lg:h-screen"
            data-testid="hero-campaign-image"
          >
            <img
              src={CAMPAIGN_LOW_BATTERY}
              alt="BananaX — Nothing should die at the wrong moment"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-ink-950/60 lg:to-ink-950"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-transparent to-transparent lg:hidden"></div>
          </motion.div>

          {/* Right: ad copy */}
          <div className="relative flex items-center px-6 md:px-10 lg:px-16 py-16 lg:py-0 -mt-32 lg:mt-0">
            <div className="space-y-8 max-w-xl">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.3 }}
                className="label-eyebrow flex items-center gap-3"
              >
                <span className="w-8 h-px bg-gold"></span>
                BananaX™ · Premium Condoms
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 0.45 }}
                className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-bone"
              >
                Nothing should die
                <br />
                at the{" "}
                <span className="italic text-gradient-gold">wrong moment.</span>
              </motion.h1>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.65 }}
                className="w-32 h-px bg-gold/50"
              ></motion.div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.7 }}
                className="space-y-2"
              >
                <p className="text-bone text-xl font-light">Power down the risks.</p>
                <p className="text-gold text-xl font-light">Power up your pleasure.</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.9 }}
                className="flex flex-wrap gap-4 pt-4"
              >
                <Link to="/shop" className="btn-primary" data-testid="hero-shop-now-btn">
                  Shop the Edit <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link to="/quiz" className="btn-ghost" data-testid="hero-explore-collection-btn">
                  Find Your Signature
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.1 }}
                className="pt-8 border-t border-white/10"
              >
                <p className="font-serif text-2xl text-bone">
                  Be Ready. <span className="text-gold">Stay Protected.</span>
                </p>
                <p className="label-eyebrow text-mist/60 mt-2">Refined by Nature</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BADGES STRIP — Official 4-icon set */}
      <section className="relative border-y border-white/5 bg-ink-900 py-10 overflow-hidden" data-testid="trust-badges-strip">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
          {TRUST_BADGES.map(({ icon: Icon, label }, i) => (
            <motion.div
              key={label}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="flex flex-col items-center text-center gap-3 group"
            >
              <div className="w-12 h-12 border border-gold/30 rounded-full flex items-center justify-center group-hover:border-gold group-hover:bg-gold/5 transition-all">
                <Icon className="w-5 h-5 text-gold stroke-[1.2]" />
              </div>
              <p className="text-[10px] sm:text-[11px] tracking-[0.28em] uppercase font-medium text-bone/90 leading-tight">
                {label}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SECOND CAMPAIGN: "Some things deserve extra protection" */}
      <section className="relative bg-ink-950" data-testid="second-campaign-section">
        <div className="max-w-[1440px] mx-auto grid lg:grid-cols-2 lg:min-h-[700px]">
          {/* Copy first on mobile, second on desktop (right column on lg) */}
          <div className="order-2 lg:order-1 flex items-center px-6 md:px-10 lg:px-16 py-20 lg:py-0">
            <div className="space-y-8 max-w-xl">
              <div className="label-eyebrow flex items-center gap-3">
                <span className="w-8 h-px bg-gold"></span>
                Real Protection · Real Life
              </div>
              <motion.h2
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
                className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-bone"
              >
                Some things deserve{" "}
                <span className="italic text-gradient-gold">extra</span> protection.
              </motion.h2>
              <div className="w-32 h-px bg-gold/50"></div>
              <div className="space-y-2">
                <p className="text-bone text-xl font-light">Not just in your phone.</p>
                <p className="text-gold text-xl font-light">In real life too.</p>
              </div>
              <p className="text-mist/80 font-light leading-relaxed text-base max-w-md">
                Every BananaX condom is electronically tested, dermatologically certified,
                and crafted from premium natural latex — engineered so the only thing that
                gets locked away is the worry.
              </p>
              <Link to="/about" className="inline-flex items-center gap-3 text-gold tracking-[0.22em] uppercase text-xs hover:text-gold-soft">
                Our craft, our promise <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/campaigns" className="inline-flex items-center gap-3 text-mist hover:text-gold tracking-[0.22em] uppercase text-xs ml-6" data-testid="home-view-campaigns-link">
                View all campaigns <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2 }}
            className="order-1 lg:order-2 relative h-[60vh] lg:h-auto"
          >
            <img
              src={CAMPAIGN_PASSWORD}
              alt="BananaX — Some things deserve extra protection"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-transparent to-ink-950/60 lg:to-ink-950"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-ink-950 via-transparent to-transparent lg:hidden"></div>
          </motion.div>
        </div>
      </section>

      {/* MASTER MANIFESTO POSTER */}
      <section className="relative bg-ink-950 py-20 lg:py-28 px-6 md:px-10" data-testid="master-manifesto-section">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-12 space-y-4">
            <div className="label-eyebrow flex items-center justify-center gap-3">
              <span className="w-8 h-px bg-gold"></span>
              The Master Poster · Vol. 00
              <span className="w-8 h-px bg-gold"></span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight">
              The entire BananaX edit, in <span className="italic text-gradient-gold">one frame</span>.
            </h2>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 1.1 }}
            className="relative max-w-5xl mx-auto"
          >
            <div className="relative aspect-square overflow-hidden border hairline-gold" data-testid="manifesto-poster-frame">
              <img
                src="/products/master-poster.jpg"
                alt="BananaX Master Poster — Protection. Pleasure. Confidence."
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/20 via-transparent to-transparent pointer-events-none"></div>

              {/* Variant box hotspots — bottom band of the master poster */}
              {[
                { slug: "longplay", name: "Longplay", left: "6%", accent: "#3B82F6" },
                { slug: "dotted-ribbed-pleasure", name: "Extra Dotted", left: "28%", accent: "#A855F7" },
                { slug: "ultra-thin-signature", name: "Ultra Thin", left: "50.5%", accent: "#E8E8E8" },
                { slug: "climax-control", name: "Climax Control", left: "73%", accent: "#EF4444" },
              ].map((v) => (
                <Link
                  key={v.slug}
                  to={`/product/${v.slug}`}
                  className="absolute group/hot"
                  style={{
                    left: v.left,
                    top: "83%",
                    width: "21%",
                    height: "15%",
                  }}
                  aria-label={`Shop ${v.name}`}
                  data-testid={`master-poster-hotspot-${v.slug}`}
                >
                  {/* Hotspot outline appears on hover */}
                  <span
                    className="absolute inset-0 border-2 opacity-0 group-hover/hot:opacity-100 transition-opacity duration-300"
                    style={{ borderColor: v.accent, boxShadow: `0 0 20px ${v.accent}66` }}
                  ></span>
                  {/* "Shop →" tag on hover */}
                  <span
                    className="absolute -top-9 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 text-[10px] tracking-[0.2em] uppercase font-medium bg-ink-950/90 border border-white/10 text-bone whitespace-nowrap opacity-0 group-hover/hot:opacity-100 translate-y-2 group-hover/hot:translate-y-0 transition-all duration-300"
                    style={{ borderColor: v.accent }}
                  >
                    Shop {v.name}
                    <ArrowRight className="w-3 h-3" style={{ color: v.accent }} />
                  </span>
                </Link>
              ))}
            </div>
            <div className="absolute -bottom-3 -right-3 w-24 h-24 border border-gold/30 hidden md:block pointer-events-none"></div>
            <div className="absolute -top-3 -left-3 w-16 h-16 bg-gold/10 hidden md:block pointer-events-none"></div>
          </motion.div>

          {/* Mobile-friendly variant strip — visible only on small screens */}
          <div
            className="lg:hidden mt-8 -mx-6 px-6 overflow-x-auto custom-scrollbar"
            data-testid="mobile-variant-strip"
          >
            <div className="flex gap-3 min-w-max">
              {[
                { slug: "longplay", name: "Longplay", accent: "#3B82F6" },
                { slug: "dotted-ribbed-pleasure", name: "Extra Dotted", accent: "#A855F7" },
                { slug: "ultra-thin-signature", name: "Ultra Thin", accent: "#E8E8E8" },
                { slug: "climax-control", name: "Climax Control", accent: "#EF4444" },
              ].map((v) => (
                <Link
                  key={v.slug}
                  to={`/product/${v.slug}`}
                  className="flex items-center gap-3 bg-ink-900 border border-white/10 active:border-gold px-4 py-3 min-w-[180px] hover:border-gold transition-colors"
                  data-testid={`mobile-variant-card-${v.slug}`}
                >
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: v.accent, boxShadow: `0 0 12px ${v.accent}` }}
                  ></span>
                  <span className="flex-1 text-bone text-sm font-light">{v.name}</span>
                  <ArrowRight className="w-3.5 h-3.5 text-gold" />
                </Link>
              ))}
            </div>
          </div>

          {/* Below-poster row: manifesto link + interaction hint */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-10">
            <p className="text-mist font-light italic text-sm tracking-wider hidden lg:block">
              Hover any box on the poster to shop the variant
            </p>
            <p className="text-mist font-light italic text-sm tracking-wider lg:hidden">
              Tap a variant above to shop
            </p>
            <span className="hidden sm:inline w-px h-4 bg-white/15"></span>
            <Link
              to="/campaigns"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-gold hover:text-gold-soft"
              data-testid="manifesto-read-link"
            >
              Read the manifesto <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <p className="text-mist font-light italic text-center mt-6 text-sm tracking-wider">
            Ananya Verma · Brand Ambassador &nbsp;·&nbsp;
            Refined by Nature · Perfected for You
          </p>
        </div>
      </section>

      {/* COLLECTION */}
      <section className="py-32 px-6 md:px-10" id="collection">
        <div className="max-w-[1440px] mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 mb-16 items-end">
            <div className="lg:col-span-7">
              <div className="label-eyebrow mb-5">The Collection</div>
              <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-none text-balance">
                Five signatures.
                <br />
                <span className="italic text-mist">One uncompromising standard.</span>
              </h2>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <Link to="/shop" className="inline-flex items-center gap-2 text-gold text-sm tracking-[0.2em] uppercase hover:text-gold-soft">
                View entire shop <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
            {products.slice(0, 6).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* BUNDLE BUILDER TEASER */}
      <section className="relative py-24 px-6 md:px-10 bg-ink-900 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/2 -left-20 w-96 h-96 rounded-full bg-gold/10 blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-burgundy/30 blur-3xl"></div>
        </div>
        <div className="relative max-w-[1440px] mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6">
            <div className="label-eyebrow flex items-center gap-3">
              <span className="w-8 h-px bg-gold"></span> New · Curate Your Own
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
              Build a <span className="italic text-gradient-gold">bespoke bundle</span>.
              <br />Save up to 20%.
            </h2>
            <p className="text-mist font-light leading-relaxed max-w-xl">
              Compose a personalized box from any of our five signatures.
              The deeper your composition, the more refined the price &mdash;
              from a <span className="text-gold">duo at 10% off</span> to the
              <span className="text-gold"> Atelier Five at 20% off</span>.
            </p>
            <Link to="/bundle" className="btn-primary mt-2" data-testid="home-bundle-cta">
              Compose your bundle <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-5 gap-2">
              {[10, 13, 15, 18, 20].map((off, i) => (
                <div
                  key={off}
                  className={`aspect-square border flex flex-col items-center justify-center text-center transition-all ${
                    i >= 2 ? "border-gold/40 bg-ink-950" : "border-white/10 bg-ink-950"
                  }`}
                >
                  <div className="num-display text-2xl md:text-3xl text-gold">{off}%</div>
                  <div className="label-eyebrow text-mist mt-1 text-[8px]">{i + 1}+ {i === 0 ? "box" : "boxes"}</div>
                </div>
              ))}
            </div>
            <p className="text-mist text-xs font-light italic text-center mt-4">
              Sample tier preview &mdash; finer increments unlocked in builder.
            </p>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="relative py-32 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0">
          <img src={DARK_SILK} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-ink-950/60"></div>
        </div>
        <div className="relative max-w-[1440px] mx-auto">
          <div className="text-center mb-20">
            <div className="label-eyebrow mb-4">Why BananaX</div>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight">
              Built for those who <span className="italic text-gradient-gold">notice</span>.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/5">
            {PILLARS.map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-ink-950 p-10 hover:bg-ink-900 transition-colors duration-500 group"
              >
                <Icon className="w-7 h-7 text-gold stroke-[1.2] mb-6 group-hover:scale-110 transition-transform" />
                <h3 className="font-serif text-xl text-bone leading-tight mb-3">{title}</h3>
                <p className="text-mist text-sm font-light leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNOLOGY */}
      <section className="py-32 px-6 md:px-10 bg-ink-900">
        <div className="max-w-[1440px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 lg:sticky lg:top-32 self-start space-y-6">
            <div className="label-eyebrow">The Atelier</div>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-none">
              Engineered, <span className="italic text-gradient-gold">not assembled</span>.
            </h2>
            <p className="text-mist font-light leading-relaxed">
              Each BananaX condom passes through four disciplined stages of craft —
              from FSC-certified plantation latex to electronic perfection testing.
            </p>
            <div className="shimmer-line w-32 h-px"></div>
          </div>
          <div className="lg:col-span-8 space-y-px">
            {TECH.map((step, i) => (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.1 }}
                className="grid grid-cols-12 gap-6 py-10 border-b border-white/5 group"
              >
                <div className="col-span-2 lg:col-span-1 num-display text-3xl text-gold/70 group-hover:text-gold transition-colors">
                  {step.n}
                </div>
                <div className="col-span-10 lg:col-span-11">
                  <h3 className="font-serif text-2xl text-bone mb-3">{step.t}</h3>
                  <p className="text-mist font-light leading-relaxed max-w-2xl">{step.d}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* BRAND STORY */}
      <section className="relative py-32 px-6 md:px-10 overflow-hidden">
        <div className="absolute inset-0">
          <img src={BRAND_STORY_BG} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink-950 via-ink-950/80 to-transparent"></div>
        </div>
        <div className="relative max-w-3xl">
          <div className="label-eyebrow mb-6">Our Philosophy</div>
          <p className="font-serif text-3xl md:text-5xl leading-tight text-balance text-bone">
            We believe protection should never compromise intimacy.
            Our mission — to craft premium intimate wellness products that deliver
            <span className="italic text-gold"> confidence, comfort, and pleasure</span> in equal measure.
          </p>
          <Link to="/about" className="inline-flex items-center gap-3 mt-12 text-gold tracking-[0.22em] uppercase text-xs hover:text-gold-soft">
            Read the manifesto <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* SUBSCRIPTION */}
      <section className="py-32 px-6 md:px-10">
        <div className="max-w-[1440px] mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-6">
            <div className="label-eyebrow">The Monthly Essentials Box</div>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight leading-none">
              Discretion, <span className="italic text-gradient-gold">delivered</span>.
            </h2>
            <p className="text-mist font-light leading-relaxed text-lg max-w-xl">
              A curated edit of BananaX signatures, dispatched to your door on the first of every month.
              Pause, skip, or cancel anytime — no questions, no friction.
            </p>
            <ul className="space-y-3 pt-2">
              {["Curated mix of all 5 signatures", "15% subscriber discount", "Exclusive limited launches", "Unmarked, discreet shipping"].map((b) => (
                <li key={b} className="flex items-start gap-3 text-bone font-light">
                  <Sparkles className="w-4 h-4 text-gold stroke-[1.4] mt-1 shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
            <Link to="/subscription" className="btn-primary mt-4" data-testid="home-subscribe-cta">
              Begin Subscription <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="relative aspect-[5/6] product-image-wrap border hairline-gold"
          >
            <img src={MONTHLY_BOX} alt="Monthly box" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-ink-950/80 via-transparent to-transparent"></div>
            <div className="absolute bottom-8 left-8 right-8">
              <div className="label-eyebrow text-gold">From ₹999 / month</div>
              <p className="font-serif text-3xl text-bone mt-2">The Essentials Box · Vol. 02</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="py-32 px-6 md:px-10 bg-ink-900">
        <div className="max-w-[1440px] mx-auto">
          <div className="text-center mb-20">
            <div className="label-eyebrow mb-4">In Their Words</div>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight">
              12,000+ <span className="italic text-gradient-gold">five-star</span> rituals.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {reviews.slice(0, 6).map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-ink-950 border border-white/5 p-8 hover:border-gold/30 transition-colors"
                data-testid={`review-card-${i}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, k) => (
                    <Star key={k} className="w-3.5 h-3.5 fill-gold text-gold stroke-[1]" />
                  ))}
                </div>
                <p className="font-serif text-xl leading-relaxed text-bone mb-6 italic">&ldquo;{r.text}&rdquo;</p>
                <div className="label-eyebrow text-mist">
                  {r.name} · {r.city}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-32 px-6 md:px-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <div className="label-eyebrow mb-4">Questions, Answered</div>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tight">Frequently Asked</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-2" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-white/10">
                <AccordionTrigger className="text-left font-serif text-xl text-bone hover:text-gold py-6">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-mist font-light leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
